import type { ReactNode } from 'react'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import BarChartIcon from '@mui/icons-material/BarChart'
import BoltIcon from '@mui/icons-material/Bolt'
import BusinessIcon from '@mui/icons-material/Business'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import FlagIcon from '@mui/icons-material/Flag'
import ListAltIcon from '@mui/icons-material/ListAlt'
import LockIcon from '@mui/icons-material/Lock'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PaymentIcon from '@mui/icons-material/Payment'
import PeopleIcon from '@mui/icons-material/People'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Action, Resource } from '../../types/permissions'
import type { Permission } from '../../types/permissions'

export interface NavItem {
  label: string
  path: string
  permission: Permission
  icon: ReactNode
  badge?: number
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

const isUpgradeOpsEnabled = process.env.REACT_APP_UPDRADE_OPS_FLAG === 'true'
const isAnalyticsEnabled = process.env.REACT_APP_ANALYTICS_FLAG === 'true'
const isUsageEnabled = process.env.REACT_APP_USAGE_FLAG === 'true'
const isMonitoringEnabled = process.env.REACT_APP_MONITORING_FLAG === 'true'
const isSecurityEnabled = process.env.REACT_APP_SECURITY_FLAG === 'true'
const isAuditLogsEnabled = process.env.REACT_APP_AUDIT_LOGS_FLAG === 'true'

export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        permission: `${Resource.Dashboard}:${Action.View}`,
        icon: <DashboardIcon fontSize="small" />,
      },
      {
        label: 'Organizations',
        path: '/organisations',
        permission: `${Resource.Organisations}:${Action.View}`,
        icon: <BusinessIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Licensing',
    items: [
      {
        label: 'Plans',
        path: '/plans',
        permission: `${Resource.Plans}:${Action.View}`,
        icon: <ListAltIcon fontSize="small" />,
      },
      {
        label: 'Billing',
        path: '/billing',
        permission: `${Resource.Billing}:${Action.View}`,
        icon: <PaymentIcon fontSize="small" />,
      },
      {
        label: 'Feature Flags',
        path: '/feature-flags',
        permission: `${Resource.FeatureFlags}:${Action.View}`,
        icon: <FlagIcon fontSize="small" />,
      },
    ],
  },
  ...(isMonitoringEnabled
    ? [
        {
          title: 'Monitoring',
          items: [
            ...(isUsageEnabled
              ? [
                  {
                    label: 'Usage',
                    path: '/usage',
                    permission: `${Resource.Usage}:${Action.View}` as Permission,
                    icon: <BarChartIcon fontSize="small" />,
                  },
                ]
              : []),
            ...(isAnalyticsEnabled
              ? [
                  {
                    label: 'Analytics',
                    path: '/analytics',
                    permission: `${Resource.Analytics}:${Action.View}` as Permission,
                    icon: <TrendingUpIcon fontSize="small" />,
                  },
                ]
              : []),
            ...(isUpgradeOpsEnabled
              ? [
                  {
                    label: 'Upgrade Ops',
                    path: '/upgrade-ops',
                    permission: `${Resource.UpgradeOps}:${Action.View}` as Permission,
                    icon: <BoltIcon fontSize="small" />,
                  },
                ]
              : []),
          ],
        },
      ]
    : []),
  {
    title: 'System',
    items: [
      ...(isAuditLogsEnabled
        ? [
            {
              label: 'Audit Logs',
              path: '/audit-logs',
              permission: `${Resource.AuditLogs}:${Action.View}` as Permission,
              icon: <DescriptionIcon fontSize="small" />,
            },
          ]
        : []),
      ...(isSecurityEnabled
        ? [
            {
              label: 'Security',
              path: '/security',
              permission: `${Resource.Security}:${Action.View}` as Permission,
              icon: <LockIcon fontSize="small" />,
            },
          ]
        : []),
    ],
  },
  {
    title: 'Alerts',
    items: [
      {
        label: 'Notifications',
        path: '/notifications',
        permission: `${Resource.Notifications}:${Action.View}`,
        icon: <NotificationsIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Users & Roles',
    items: [
      {
        label: 'User Management',
        path: '/user-management',
        permission: `${Resource.Users}:${Action.View}`,
        icon: <PeopleIcon fontSize="small" />,
      },
      {
        label: 'Role Management',
        path: '/role-management',
        permission: `${Resource.Roles}:${Action.View}`,
        icon: <AdminPanelSettingsIcon fontSize="small" />,
      },
    ],
  },
]
